/**
 * Template Seed Data
 * Phase 2 Sprint 13: Template Infrastructure
 * 
 * 12 production-ready templates across 5 categories
 */

import { Template } from '../types/template';

export const TEMPLATE_SEEDS: Template[] = [
  // ============ EDUCATION CATEGORY (3 templates) ============
  {
    id: 'edu-course-intro',
    title: 'Course Introduction Video',
    description: 'Perfect for introducing your online course with a professional intro, overview, and call-to-action',
    category: 'education',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'Welcome & Hook',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#3B82F6', '#1E40AF'] },
        scriptKey: 'intro-hook',
        notes: 'Energetic opening - smile, make eye contact. State your name and course clearly. Keep it punchy and engaging.',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Course Overview',
        suggestedDuration: 120,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'course-overview',
        notes: 'Share screen to show course outline or slides. Speak clearly about what the course covers. Pace: steady and informative.',
        tags: ['content']
      },
      {
        order: 2,
        title: 'What You Will Learn',
        suggestedDuration: 90,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'learning-objectives',
        notes: 'List 3-5 specific outcomes. Use bullet points or numbered list on screen. Be concrete about benefits students will gain.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Call to Action',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#10B981', '#059669'] },
        scriptKey: 'cta-enroll',
        notes: 'Direct to camera. Confident and inviting tone. Mention enrollment link, pricing, or special offer. End with enthusiasm.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'intro-hook',
        sceneTitle: 'Welcome & Hook',
        content: 'Welcome to [Course Name]! I\'m [Your Name], and I\'m excited to help you master [Topic].',
        defaultExample: 'Welcome to React Mastery! I\'m Sarah Chen, and I\'m excited to help you master modern React development.',
        customizationNote: 'Replace [Course Name] and [Your Name] with your details'
      },
      {
        key: 'course-overview',
        sceneTitle: 'Course Overview',
        content: 'In this course, you\'ll learn [Main Topics]. We\'ll cover everything from [Basics] to [Advanced Concepts].',
        defaultExample: 'In this course, you\'ll learn React Hooks, Context API, and performance optimization. We\'ll cover everything from useState basics to advanced custom hooks.',
        customizationNote: 'List your course modules and key topics'
      },
      {
        key: 'learning-objectives',
        sceneTitle: 'What You Will Learn',
        content: 'By the end of this course, you\'ll be able to: [Objective 1], [Objective 2], [Objective 3].',
        defaultExample: 'By the end of this course, you\'ll be able to: build production-ready React apps, optimize performance, and implement complex state management.',
        customizationNote: 'List 3-5 specific learning outcomes'
      },
      {
        key: 'cta-enroll',
        sceneTitle: 'Call to Action',
        content: 'Ready to get started? Enroll now and join [X] students already learning [Topic]!',
        defaultExample: 'Ready to get started? Enroll now and join 10,000 students already mastering React!',
        customizationNote: 'Add enrollment details and student count'
      }
    ],
    exampleOutput: {
      title: 'Professional 4-minute course introduction',
      duration: 270,
      whatUserGets: 'A polished intro video with hook, overview, learning objectives, and enrollment CTA'
    },
    backgroundImage: '/templates/backgrounds/course-intro-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'courseName',
          label: 'Course Name',
          placeholder: 'e.g., React Mastery',
          helpText: 'The title of your course',
          type: 'text',
          required: true,
          aiVariable: 'courseName',
          group: 'basics',
          maxLength: 100
        },
        {
          id: 'instructorName',
          label: 'Your Name',
          placeholder: 'e.g., Sarah Chen',
          helpText: 'How you want to be introduced',
          type: 'text',
          required: true,
          aiVariable: 'instructorName',
          group: 'basics',
          maxLength: 60
        },
        {
          id: 'mainTopic',
          label: 'Main Topic',
          placeholder: 'e.g., modern React development',
          helpText: 'The core skill or subject students will master',
          type: 'text',
          required: true,
          aiVariable: 'mainTopic',
          group: 'content',
          maxLength: 100
        },
        {
          id: 'keyModules',
          label: 'Key Modules (comma-separated)',
          placeholder: 'e.g., Hooks, Context API, Performance',
          helpText: 'List 3-5 main topics covered in the course',
          type: 'textarea',
          required: true,
          aiVariable: 'keyModules',
          group: 'content',
          maxLength: 300
        },
        {
          id: 'learningOutcomes',
          label: 'Learning Outcomes',
          placeholder: 'e.g., build production apps, optimize performance',
          helpText: 'What students will be able to do after completing the course',
          type: 'textarea',
          required: true,
          aiVariable: 'learningOutcomes',
          group: 'content',
          maxLength: 400
        },
        {
          id: 'studentCount',
          label: 'Student Count (optional)',
          placeholder: 'e.g., 10,000',
          helpText: 'Social proof for your CTA (leave blank if new course)',
          type: 'text',
          required: false,
          aiVariable: 'studentCount',
          group: 'cta',
          maxLength: 20
        },
        {
          id: 'specialOffer',
          label: 'Special Offer (optional)',
          placeholder: 'e.g., 30% off this week',
          helpText: 'Any current promotion or discount',
          type: 'text',
          required: false,
          aiVariable: 'specialOffer',
          group: 'cta',
          maxLength: 100
        }
      ]
    }
  },

  {
    id: 'edu-tutorial',
    title: 'Tutorial Explainer',
    description: 'Step-by-step tutorial format with intro, demonstration, and recap',
    category: 'education',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'Introduction',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#8B5CF6', '#6D28D9'] },
        scriptKey: 'tutorial-intro',
        notes: 'Set expectations for the tutorial. Explain what viewers will learn and why it matters. Friendly and approachable tone.',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Step-by-Step Demo',
        suggestedDuration: 180,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#111827'] },
        scriptKey: 'tutorial-steps',
        notes: 'Screen share required. Go slow, explain each step clearly. Pause between steps. Show, don\'t just tell. Use slow, clear narration.',
        tags: ['demo', 'content']
      },
      {
        order: 2,
        title: 'Key Takeaways',
        suggestedDuration: 45,
        layoutPreset: 'Preset_CamTopRight',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'tutorial-recap',
        notes: 'Summarize the 3 most important points. Show completed result on screen. Reinforce what was learned.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Next Steps',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#10B981', '#059669'] },
        scriptKey: 'tutorial-next',
        notes: 'Encourage viewers to try it themselves. Mention resources in description. Invite questions and feedback. Warm sign-off.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'tutorial-intro',
        sceneTitle: 'Introduction',
        content: 'Today, we\'re going to learn [Tutorial Topic]. This will help you [Benefit].',
        defaultExample: 'Today, we\'re going to learn how to deploy a React app to Vercel. This will help you ship your projects faster.',
        teleprompterSettings: { speed: 150, fontSize: 24 }
      },
      {
        key: 'tutorial-steps',
        sceneTitle: 'Step-by-Step Demo',
        content: 'Let\'s start with step 1: [Action]. Then we\'ll move to step 2: [Action]. Finally, step 3: [Action].',
        defaultExample: 'Let\'s start with step 1: Install Vercel CLI. Then we\'ll move to step 2: Configure deployment settings. Finally, step 3: Deploy and verify.',
        teleprompterSettings: { speed: 130, fontSize: 22 }
      },
      {
        key: 'tutorial-recap',
        sceneTitle: 'Key Takeaways',
        content: 'To recap: we covered [Point 1], [Point 2], and [Point 3]. You now know how to [Achievement].',
        defaultExample: 'To recap: we covered Vercel setup, deployment configuration, and production optimization. You now know how to deploy React apps in minutes.',
        teleprompterSettings: { speed: 140, fontSize: 24 }
      },
      {
        key: 'tutorial-next',
        sceneTitle: 'Next Steps',
        content: 'Try this yourself! Check the description for resources. See you in the next tutorial!',
        defaultExample: 'Try deploying your own app! Check the description for my deployment checklist. See you in the next tutorial!',
        teleprompterSettings: { speed: 150, fontSize: 24 }
      }
    ],
    exampleOutput: {
      title: '4-5 minute tutorial with step-by-step demonstration',
      duration: 285,
      whatUserGets: 'Complete tutorial flow: intro → demo → recap → next steps'
    },
    backgroundImage: '/templates/backgrounds/tutorial-explainer-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'tutorialTopic',
          aiVariable: 'tutorialTopic',
          label: 'Tutorial Topic',
          type: 'text',
          required: true,
          placeholder: 'e.g., Deploy a React app to Vercel',
          tooltip: 'What will you teach?',
          group: 'basics'
        },
        {
          id: 'benefit',
          aiVariable: 'benefit',
          label: 'Main Benefit',
          type: 'text',
          required: true,
          placeholder: 'e.g., Ship your projects faster',
          tooltip: 'What will viewers be able to do after watching?',
          group: 'basics'
        },
        {
          id: 'steps',
          aiVariable: 'steps',
          label: 'Key Steps (3-5)',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., 1. Install CLI\\n2. Configure settings\\n3. Deploy',
          tooltip: 'List the main steps you will demonstrate',
          group: 'content'
        },
        {
          id: 'targetAudience',
          aiVariable: 'targetAudience',
          label: 'Target Audience',
          type: 'select',
          required: false,
          options: [
            { value: 'beginners', label: 'Beginners' },
            { value: 'intermediate', label: 'Intermediate' },
            { value: 'advanced', label: 'Advanced' }
          ],
          group: 'basics'
        },
        {
          id: 'resources',
          aiVariable: 'resources',
          label: 'Resources to Mention',
          type: 'text',
          required: false,
          placeholder: 'e.g., GitHub repo, documentation link',
          group: 'cta'
        }
      ]
    }
  },

  {
    id: 'edu-student-testimonial',
    title: 'Student Testimonial',
    description: 'Capture authentic student feedback with structured prompts',
    category: 'education',
    aspectRatio: '9:16',
    scenes: [
      {
        order: 0,
        title: 'Introduction',
        suggestedDuration: 20,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'testimonial-intro',
        notes: 'Quick intro - say your name and the course you completed. Authentic and natural, not rehearsed.',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Before the Course',
        suggestedDuration: 40,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'testimonial-before',
        notes: 'Share genuine struggles or challenges. Be specific about what you found difficult. Relatable and honest tone.',
        tags: ['content']
      },
      {
        order: 2,
        title: 'Key Learnings',
        suggestedDuration: 60,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'testimonial-learning',
        notes: 'Highlight 1-2 specific things that helped most. Mention instructor or teaching style. Show enthusiasm but stay authentic.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Results & Recommendation',
        suggestedDuration: 40,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'testimonial-results',
        notes: 'Share concrete results or outcomes. End with genuine recommendation. Direct to camera, confident and satisfied tone.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'testimonial-intro',
        sceneTitle: 'Introduction',
        content: 'Hi, I\'m [Your Name], and I recently completed [Course Name].',
        defaultExample: 'Hi, I\'m Alex Rodriguez, and I recently completed the Advanced JavaScript Bootcamp.'
      },
      {
        key: 'testimonial-before',
        sceneTitle: 'Before the Course',
        content: 'Before taking this course, I struggled with [Challenge]. I wanted to [Goal].',
        defaultExample: 'Before taking this course, I struggled with async programming. I wanted to build real-world applications confidently.'
      },
      {
        key: 'testimonial-learning',
        sceneTitle: 'Key Learnings',
        content: 'The most valuable thing I learned was [Key Learning]. The instructor\'s approach to [Topic] was especially helpful.',
        defaultExample: 'The most valuable thing I learned was Promises and async/await. The instructor\'s approach to error handling was especially helpful.'
      },
      {
        key: 'testimonial-results',
        sceneTitle: 'Results & Recommendation',
        content: 'Now I can [Achievement]. I highly recommend this course to anyone who wants to [Goal].',
        defaultExample: 'Now I can build full-stack applications with confidence. I highly recommend this course to anyone who wants to level up their JavaScript skills.'
      }
    ],
    exampleOutput: {
      title: '2-3 minute authentic student testimonial',
      duration: 160,
      whatUserGets: 'Structured testimonial: intro → challenges → learnings → results'
    },
    backgroundImage: '/templates/backgrounds/student-testimonial-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'studentName',
          aiVariable: 'studentName',
          label: 'Your Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., Alex Rodriguez',
          group: 'basics'
        },
        {
          id: 'courseName',
          aiVariable: 'courseName',
          label: 'Course Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., Advanced JavaScript Bootcamp',
          group: 'basics'
        },
        {
          id: 'challengeBefore',
          aiVariable: 'challengeBefore',
          label: 'Challenge Before the Course',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., I struggled with async programming and building real apps',
          tooltip: 'What did you struggle with before?',
          group: 'content'
        },
        {
          id: 'keyLearning',
          aiVariable: 'keyLearning',
          label: 'Most Valuable Learning',
          type: 'text',
          required: true,
          placeholder: 'e.g., Promises and async/await patterns',
          group: 'content'
        },
        {
          id: 'achievement',
          aiVariable: 'achievement',
          label: 'What Can You Do Now?',
          type: 'text',
          required: true,
          placeholder: 'e.g., Build full-stack applications with confidence',
          group: 'benefits'
        }
      ]
    }
  },

  // ============ BUSINESS CATEGORY (3 templates) ============
  {
    id: 'biz-pitch-deck',
    title: 'Pitch Deck Presentation',
    description: 'Present your startup pitch with problem, solution, market, and ask',
    category: 'business',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'The Problem',
        suggestedDuration: 45,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#0F172A'] },
        scriptKey: 'pitch-problem',
        notes: 'Start strong - quantify the problem with data. Show pain points clearly. Use slides with key stats. Confident, authoritative tone.',
        tags: ['intro', 'content']
      },
      {
        order: 1,
        title: 'Our Solution',
        suggestedDuration: 60,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#1E293B'] },
        scriptKey: 'pitch-solution',
        notes: 'Demo your product or show key features. Explain unique value prop clearly. Show, don\'t just tell. Pace: enthusiastic but controlled.',
        tags: ['demo', 'content']
      },
      {
        order: 2,
        title: 'Market Opportunity',
        suggestedDuration: 45,
        layoutPreset: 'Preset_CamBottomRight',
        background: { type: 'solid', colors: ['#334155'] },
        scriptKey: 'pitch-market',
        notes: 'Present market size with slides/charts. Mention TAM/SAM/SOM. Show traction if available. Data-driven and factual tone.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'The Ask',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#3B82F6', '#1E40AF'] },
        scriptKey: 'pitch-ask',
        notes: 'Direct to camera. State investment amount and use of funds clearly. Show confidence. End with invitation to discuss further.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'pitch-problem',
        sceneTitle: 'The Problem',
        content: '[Target Market] struggles with [Problem]. This costs them [Impact in $ or time].',
        defaultExample: 'Small businesses struggle with manual invoice tracking. This costs them 10+ hours per week and thousands in late payments.',
        teleprompterSettings: { speed: 140, fontSize: 24 }
      },
      {
        key: 'pitch-solution',
        sceneTitle: 'Our Solution',
        content: '[Product Name] solves this by [How]. Our approach is [Unique Value Prop].',
        defaultExample: 'InvoiceFlow solves this by automating invoice tracking and payment reminders. Our AI-powered approach reduces late payments by 80%.',
        teleprompterSettings: { speed: 150, fontSize: 24 }
      },
      {
        key: 'pitch-market',
        sceneTitle: 'Market Opportunity',
        content: 'The [Market] is worth [$X billion]. We\'re targeting [Segment] with [TAM/SAM].',
        defaultExample: 'The SMB software market is worth $500 billion. We\'re targeting 2M small businesses with $8B addressable market.',
        teleprompterSettings: { speed: 140, fontSize: 22 }
      },
      {
        key: 'pitch-ask',
        sceneTitle: 'The Ask',
        content: 'We\'re raising [$Amount] to [Use of Funds]. Let\'s talk about how you can join us.',
        defaultExample: 'We\'re raising $2M to scale our sales team and expand to EU markets. Let\'s talk about how you can join us.',
        teleprompterSettings: { speed: 150, fontSize: 26 }
      }
    ],
    exampleOutput: {
      title: '3-minute investor pitch deck',
      duration: 180,
      whatUserGets: 'Professional pitch: problem → solution → market → ask'
    },
    backgroundImage: '/templates/backgrounds/pitch-deck-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'productName',
          aiVariable: 'productName',
          label: 'Product/Company Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., InvoiceFlow',
          group: 'basics'
        },
        {
          id: 'targetMarket',
          aiVariable: 'targetMarket',
          label: 'Target Market',
          type: 'text',
          required: true,
          placeholder: 'e.g., Small businesses, Enterprise SaaS',
          group: 'basics'
        },
        {
          id: 'problem',
          aiVariable: 'problem',
          label: 'Problem You Solve',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., Manual invoice tracking costs 10+ hours/week',
          tooltip: 'What pain point does your product address?',
          group: 'content'
        },
        {
          id: 'solution',
          aiVariable: 'solution',
          label: 'Your Solution',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., AI-powered automation that reduces late payments by 80%',
          group: 'content'
        },
        {
          id: 'marketSize',
          aiVariable: 'marketSize',
          label: 'Market Size',
          type: 'text',
          required: false,
          placeholder: 'e.g., $500B SMB software market',
          group: 'content'
        },
        {
          id: 'fundingAsk',
          aiVariable: 'fundingAsk',
          label: 'Funding Ask',
          type: 'text',
          required: true,
          placeholder: 'e.g., $2M for sales team expansion',
          group: 'cta'
        }
      ]
    }
  },

  {
    id: 'biz-product-demo',
    title: 'Product Demo',
    description: 'Showcase your product with hook, demo, benefits, and call-to-action',
    category: 'business',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'Hook',
        suggestedDuration: 20,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#EC4899', '#BE185D'] },
        scriptKey: 'demo-hook',
        notes: 'Grab attention immediately with a bold question or statement. Energy is key. Make them want to watch more.',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Product Demo',
        suggestedDuration: 120,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#111827'] },
        scriptKey: 'demo-walkthrough',
        notes: 'Screen share to show product in action. Walk through 3 key features. Keep it simple - focus on value, not complexity. Narrate clearly.',
        tags: ['demo', 'content']
      },
      {
        order: 2,
        title: 'Key Benefits',
        suggestedDuration: 40,
        layoutPreset: 'Preset_CamTopRight',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'demo-benefits',
        notes: 'Highlight ROI and time savings. Use specific numbers (hours saved, % increase, etc.). Show benefit slides or graphics.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Call to Action',
        suggestedDuration: 20,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#10B981', '#059669'] },
        scriptKey: 'demo-cta',
        notes: 'Strong CTA - free trial, demo booking, or signup. Remove friction. Mention no credit card if applicable. Clear next step.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'demo-hook',
        sceneTitle: 'Hook',
        content: 'What if you could [Desired Outcome] in [Time]? Let me show you how.',
        defaultExample: 'What if you could automate your entire sales pipeline in 5 minutes? Let me show you how.',
        teleprompterSettings: { speed: 160, fontSize: 26 }
      },
      {
        key: 'demo-walkthrough',
        sceneTitle: 'Product Demo',
        content: 'Here\'s how it works: First, [Step 1]. Then, [Step 2]. Finally, [Step 3]. It\'s that simple.',
        defaultExample: 'Here\'s how it works: First, connect your CRM. Then, set up automation rules. Finally, watch leads move through your pipeline automatically. It\'s that simple.',
        teleprompterSettings: { speed: 130, fontSize: 22 }
      },
      {
        key: 'demo-benefits',
        sceneTitle: 'Key Benefits',
        content: 'With [Product], you\'ll save [Time/Money], increase [Metric], and eliminate [Pain Point].',
        defaultExample: 'With SalesPilot, you\'ll save 20 hours per week, increase conversion by 40%, and eliminate manual data entry.',
        teleprompterSettings: { speed: 150, fontSize: 24 }
      },
      {
        key: 'demo-cta',
        sceneTitle: 'Call to Action',
        content: 'Start your free trial today. No credit card required.',
        defaultExample: 'Start your 14-day free trial today. No credit card required.',
        teleprompterSettings: { speed: 150, fontSize: 26 }
      }
    ],
    exampleOutput: {
      title: '3-minute product demo video',
      duration: 200,
      whatUserGets: 'Complete demo flow: hook → walkthrough → benefits → CTA'
    },
    backgroundImage: '/templates/backgrounds/product-demo-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'productName',
          label: 'Product Name',
          placeholder: 'e.g., SalesPilot',
          helpText: 'The name of your product or service',
          type: 'text',
          required: true,
          aiVariable: 'productName',
          group: 'basics',
          maxLength: 60
        },
        {
          id: 'desiredOutcome',
          label: 'Main Outcome',
          placeholder: 'e.g., automate your entire sales pipeline',
          helpText: 'What result does your product deliver?',
          type: 'text',
          required: true,
          aiVariable: 'desiredOutcome',
          group: 'hook',
          maxLength: 150
        },
        {
          id: 'timeToValue',
          label: 'Time to Value',
          placeholder: 'e.g., 5 minutes, one click, instantly',
          helpText: 'How quickly can users see results?',
          type: 'text',
          required: true,
          aiVariable: 'timeToValue',
          group: 'hook',
          maxLength: 50
        },
        {
          id: 'keyFeatures',
          label: 'Key Features (3 steps)',
          placeholder: 'e.g., 1. Connect your CRM, 2. Set automation rules, 3. Watch leads flow',
          helpText: 'List 3 main features or steps to show in the demo',
          type: 'textarea',
          required: true,
          aiVariable: 'keyFeatures',
          group: 'demo',
          maxLength: 400
        },
        {
          id: 'timeSaved',
          label: 'Time/Money Saved',
          placeholder: 'e.g., 20 hours per week',
          helpText: 'Quantify the efficiency gain',
          type: 'text',
          required: true,
          aiVariable: 'timeSaved',
          group: 'benefits',
          maxLength: 60
        },
        {
          id: 'metricImproved',
          label: 'Metric Improved',
          placeholder: 'e.g., conversion by 40%',
          helpText: 'What measurable outcome improves?',
          type: 'text',
          required: true,
          aiVariable: 'metricImproved',
          group: 'benefits',
          maxLength: 60
        },
        {
          id: 'painPointEliminated',
          label: 'Pain Point Eliminated',
          placeholder: 'e.g., manual data entry',
          helpText: 'What frustration does your product remove?',
          type: 'text',
          required: true,
          aiVariable: 'painPointEliminated',
          group: 'benefits',
          maxLength: 100
        },
        {
          id: 'ctaOffer',
          label: 'CTA Offer',
          placeholder: 'e.g., 14-day free trial, no credit card required',
          helpText: 'What should viewers do next?',
          type: 'text',
          required: true,
          aiVariable: 'ctaOffer',
          group: 'cta',
          maxLength: 100
        }
      ]
    }
  },

  {
    id: 'biz-company-culture',
    title: 'Company Culture Video',
    description: 'Showcase your company values, team, and work environment',
    category: 'business',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'Welcome',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#3B82F6', '#1E40AF'] },
        scriptKey: 'culture-welcome',
        notes: 'Warm welcome - introduce yourself and role. Authentic enthusiasm about the company. Set positive tone for the video.',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Our Values',
        suggestedDuration: 60,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'culture-values',
        notes: 'Share 3-4 core company values. Show slides with value statements. Explain what each means in practice with examples.',
        tags: ['content']
      },
      {
        order: 2,
        title: 'Team & Environment',
        suggestedDuration: 60,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'culture-team',
        notes: 'Show team photos/workspace if available. Mention perks and benefits. Highlight what makes work environment unique. Genuine and inviting.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Join Us',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#10B981', '#059669'] },
        scriptKey: 'culture-join',
        notes: 'Direct careers page CTA. Express excitement about new team members. Welcoming and inclusive tone. End with mission statement.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'culture-welcome',
        sceneTitle: 'Welcome',
        content: 'Welcome to [Company Name]! I\'m [Your Name], and I\'m excited to share what makes our team special.',
        defaultExample: 'Welcome to TechFlow! I\'m Jordan Lee, and I\'m excited to share what makes our team special.'
      },
      {
        key: 'culture-values',
        sceneTitle: 'Our Values',
        content: 'We believe in [Value 1], [Value 2], and [Value 3]. These values guide everything we do.',
        defaultExample: 'We believe in innovation, collaboration, and customer obsession. These values guide everything we do.'
      },
      {
        key: 'culture-team',
        sceneTitle: 'Team & Environment',
        content: 'Our team of [X] people works in [Environment]. We offer [Benefits] and foster [Culture].',
        defaultExample: 'Our team of 50 people works in a hybrid environment. We offer unlimited PTO, learning budgets, and foster a culture of continuous growth.'
      },
      {
        key: 'culture-join',
        sceneTitle: 'Join Us',
        content: 'We\'re hiring! Check out our open positions and join us in [Mission].',
        defaultExample: 'We\'re hiring! Check out our open positions and join us in building the future of work.'
      }
    ],
    exampleOutput: {
      title: '3-minute company culture showcase',
      duration: 180,
      whatUserGets: 'Culture video: welcome → values → team → careers CTA'
    },
    backgroundImage: '/templates/backgrounds/company-culture-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'companyName',
          aiVariable: 'companyName',
          label: 'Company Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., TechFlow',
          group: 'basics'
        },
        {
          id: 'speakerName',
          aiVariable: 'speakerName',
          label: 'Your Name & Role',
          type: 'text',
          required: true,
          placeholder: 'e.g., Jordan Lee, People Operations Lead',
          group: 'basics'
        },
        {
          id: 'coreValues',
          aiVariable: 'coreValues',
          label: 'Core Values (3-4)',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., Innovation, Collaboration, Customer Obsession',
          tooltip: 'What values guide your company?',
          group: 'content'
        },
        {
          id: 'teamSize',
          aiVariable: 'teamSize',
          label: 'Team Size',
          type: 'text',
          required: false,
          placeholder: 'e.g., 50 people',
          group: 'content'
        },
        {
          id: 'workEnvironment',
          aiVariable: 'workEnvironment',
          label: 'Work Environment',
          type: 'select',
          required: true,
          options: [
            { value: 'remote', label: 'Fully Remote' },
            { value: 'hybrid', label: 'Hybrid' },
            { value: 'office', label: 'Office-Based' }
          ],
          group: 'content'
        },
        {
          id: 'keyBenefits',
          aiVariable: 'keyBenefits',
          label: 'Key Benefits & Perks',
          type: 'textarea',
          required: false,
          placeholder: 'e.g., Unlimited PTO, learning budgets, stock options',
          group: 'benefits'
        },
        {
          id: 'mission',
          aiVariable: 'mission',
          label: 'Company Mission',
          type: 'text',
          required: false,
          placeholder: 'e.g., Building the future of work',
          group: 'cta'
        }
      ]
    }
  },

  // ============ CONTENT CREATION CATEGORY (2 templates) ============
  {
    id: 'content-youtube-opener',
    title: 'YouTube Video Opener',
    description: 'Energetic opener for YouTube with hook, intro, and preview',
    category: 'content',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'Hook',
        suggestedDuration: 15,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#F59E0B', '#D97706'] },
        scriptKey: 'yt-hook',
        notes: 'FAST and punchy. Lead with shocking stat or question. Maximum energy. Goal: stop the scroll.',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Intro',
        suggestedDuration: 20,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#EF4444', '#DC2626'] },
        scriptKey: 'yt-intro',
        notes: 'Quick personal intro. State topic clearly. Maintain high energy. Speak directly to camera like talking to a friend.',
        tags: ['intro']
      },
      {
        order: 2,
        title: 'What\'s Coming',
        suggestedDuration: 25,
        layoutPreset: 'Preset_CamTopRight',
        background: { type: 'solid', colors: ['#111827'] },
        scriptKey: 'yt-preview',
        notes: 'Tease 3 key points they\'ll learn. Build anticipation. End with "let\'s dive in!" or similar transition. Energetic throughout.',
        tags: ['content', 'transition']
      }
    ],
    scripts: [
      {
        key: 'yt-hook',
        sceneTitle: 'Hook',
        content: '[Shocking fact or question]. Stick around to find out [Promise].',
        defaultExample: '73% of creators quit in the first year. Stick around to find out the 3 secrets that keep successful creators going.',
        teleprompterSettings: { speed: 170, fontSize: 26 }
      },
      {
        key: 'yt-intro',
        sceneTitle: 'Intro',
        content: 'Hey everyone, [Your Name] here! Today we\'re talking about [Topic].',
        defaultExample: 'Hey everyone, Casey here! Today we\'re talking about sustainable content creation strategies.',
        teleprompterSettings: { speed: 160, fontSize: 24 }
      },
      {
        key: 'yt-preview',
        sceneTitle: 'What\'s Coming',
        content: 'In this video, I\'ll show you [Benefit 1], [Benefit 2], and [Benefit 3]. Let\'s dive in!',
        defaultExample: 'In this video, I\'ll show you how to batch content, avoid burnout, and 10x your productivity. Let\'s dive in!',
        teleprompterSettings: { speed: 160, fontSize: 24 }
      }
    ],
    exampleOutput: {
      title: '1-minute YouTube opener',
      duration: 60,
      whatUserGets: 'Energetic opener: hook → intro → preview'
    },
    backgroundImage: '/templates/backgrounds/youtube-opener-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'creatorName',
          aiVariable: 'creatorName',
          label: 'Your Channel/Creator Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., Casey',
          group: 'basics'
        },
        {
          id: 'videoTopic',
          aiVariable: 'videoTopic',
          label: 'Video Topic',
          type: 'text',
          required: true,
          placeholder: 'e.g., Sustainable content creation strategies',
          group: 'basics'
        },
        {
          id: 'hookStatement',
          aiVariable: 'hookStatement',
          label: 'Hook (Shocking Stat or Question)',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., 73% of creators quit in the first year',
          tooltip: 'What will make viewers stop scrolling?',
          group: 'hook'
        },
        {
          id: 'keyBenefits',
          aiVariable: 'keyBenefits',
          label: 'What Viewers Will Learn (3 things)',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., How to batch content, avoid burnout, 10x productivity',
          group: 'content'
        }
      ]
    }
  },

  {
    id: 'content-social-promo',
    title: 'Social Media Promo',
    description: 'Short, punchy promo for Instagram/LinkedIn with problem → solution → CTA',
    category: 'content',
    aspectRatio: '9:16',
    scenes: [
      {
        order: 0,
        title: 'Problem',
        suggestedDuration: 15,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'solid', colors: ['#DC2626'] },
        scriptKey: 'social-problem',
        notes: 'Call out the pain point immediately. Relatable frustration. Fast-paced. Make them nod "yes, that\'s me!"',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Solution',
        suggestedDuration: 25,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#3B82F6', '#1E40AF'] },
        scriptKey: 'social-solution',
        notes: 'Present your solution as the answer. Focus on the benefit, not the how. Quick and punchy. Show value immediately.',
        tags: ['content']
      },
      {
        order: 2,
        title: 'Call to Action',
        suggestedDuration: 15,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#10B981', '#059669'] },
        scriptKey: 'social-cta',
        notes: 'Ultra-clear CTA. "Link in bio" or equivalent. Friendly invitation. End on high energy note.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'social-problem',
        sceneTitle: 'Problem',
        content: 'Tired of [Problem]? You\'re not alone.',
        defaultExample: 'Tired of posting on social media with zero engagement? You\'re not alone.',
        teleprompterSettings: { speed: 180, fontSize: 28 }
      },
      {
        key: 'social-solution',
        sceneTitle: 'Solution',
        content: 'That\'s why I created [Solution]. It helps you [Benefit] in just [Time].',
        defaultExample: 'That\'s why I created the Content Accelerator. It helps you create 30 days of content in just 2 hours.',
        teleprompterSettings: { speed: 170, fontSize: 26 }
      },
      {
        key: 'social-cta',
        sceneTitle: 'Call to Action',
        content: 'Link in bio. Let\'s grow together!',
        defaultExample: 'Free template in my link in bio. Let\'s grow together!',
        teleprompterSettings: { speed: 180, fontSize: 28 }
      }
    ],
    exampleOutput: {
      title: '55-second social media promo',
      duration: 55,
      whatUserGets: 'Punchy promo: problem → solution → CTA'
    },
    backgroundImage: '/templates/backgrounds/social-promo-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'problem',
          aiVariable: 'problem',
          label: 'The Problem',
          type: 'text',
          required: true,
          placeholder: 'e.g., Posting on social media with zero engagement',
          tooltip: 'What frustration does your audience have?',
          group: 'hook'
        },
        {
          id: 'solutionName',
          aiVariable: 'solutionName',
          label: 'Your Solution Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., Content Accelerator',
          group: 'content'
        },
        {
          id: 'benefit',
          aiVariable: 'benefit',
          label: 'Main Benefit',
          type: 'text',
          required: true,
          placeholder: 'e.g., Create 30 days of content in just 2 hours',
          group: 'content'
        },
        {
          id: 'ctaType',
          aiVariable: 'ctaType',
          label: 'Call to Action',
          type: 'select',
          required: true,
          options: [
            { value: 'link-in-bio', label: 'Link in Bio' },
            { value: 'dm-me', label: 'DM Me' },
            { value: 'comment-below', label: 'Comment Below' },
            { value: 'swipe-up', label: 'Swipe Up' }
          ],
          group: 'cta'
        }
      ]
    }
  },

  // ============ DEVELOPER CATEGORY (2 templates) ============
  {
    id: 'dev-code-tutorial',
    title: 'Code Tutorial',
    description: 'Technical tutorial with problem, implementation, and explanation',
    category: 'developer',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'The Problem',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'code-problem',
        notes: 'Set technical context. Explain the problem developers face. Be clear about prerequisites. Technical but accessible tone.',
        tags: ['intro', 'content']
      },
      {
        order: 1,
        title: 'The Code',
        suggestedDuration: 180,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#0F172A'] },
        scriptKey: 'code-implementation',
        notes: 'Screen share with code editor. Type slowly and explain as you go. Pause between sections. Show complete, working code. Use syntax highlighting.',
        tags: ['demo', 'content']
      },
      {
        order: 2,
        title: 'How It Works',
        suggestedDuration: 60,
        layoutPreset: 'Preset_CamBottomRight',
        background: { type: 'solid', colors: ['#111827'] },
        scriptKey: 'code-explanation',
        notes: 'Explain the "why" behind the code. Break down complex parts. Mention edge cases or gotchas. Show diagram if helpful.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Next Steps',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#8B5CF6', '#6D28D9'] },
        scriptKey: 'code-next',
        notes: 'Link to full code (GitHub, etc.). Suggest next learning steps. Invite questions. Supportive, encouraging tone.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'code-problem',
        sceneTitle: 'The Problem',
        content: 'Today we\'re solving [Problem]. This is a common challenge when [Context].',
        defaultExample: 'Today we\'re solving infinite scroll in React. This is a common challenge when building data-heavy UIs.',
        teleprompterSettings: { speed: 140, fontSize: 22 }
      },
      {
        key: 'code-implementation',
        sceneTitle: 'The Code',
        content: 'Let\'s build this step by step. First, [Step 1]. Then, [Step 2]. Finally, [Step 3].',
        defaultExample: 'Let\'s build this step by step. First, we\'ll set up the Intersection Observer. Then, we\'ll handle pagination. Finally, we\'ll optimize with React.memo.',
        teleprompterSettings: { speed: 120, fontSize: 20 }
      },
      {
        key: 'code-explanation',
        sceneTitle: 'How It Works',
        content: 'Here\'s what\'s happening: [Technical Explanation]. This approach is better because [Reason].',
        defaultExample: 'Here\'s what\'s happening: We observe the last element, trigger a callback, and batch fetch more data. This approach is better because it\'s performant and doesn\'t require scroll listeners.',
        teleprompterSettings: { speed: 130, fontSize: 22 }
      },
      {
        key: 'code-next',
        sceneTitle: 'Next Steps',
        content: 'Try implementing this in your project. Code is in the description. Questions? Drop a comment!',
        defaultExample: 'Try implementing this in your project. Full code is in the description. Questions? Drop a comment!',
        teleprompterSettings: { speed: 150, fontSize: 24 }
      }
    ],
    exampleOutput: {
      title: '5-minute coding tutorial',
      duration: 300,
      whatUserGets: 'Technical tutorial: problem → code → explanation → resources'
    },
    backgroundImage: '/templates/backgrounds/code-tutorial-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'codingProblem',
          aiVariable: 'codingProblem',
          label: 'What Problem Are You Solving?',
          type: 'text',
          required: true,
          placeholder: 'e.g., Infinite scroll in React',
          group: 'basics'
        },
        {
          id: 'context',
          aiVariable: 'context',
          label: 'When Does This Problem Occur?',
          type: 'text',
          required: true,
          placeholder: 'e.g., When building data-heavy UIs',
          group: 'basics'
        },
        {
          id: 'implementationSteps',
          aiVariable: 'implementationSteps',
          label: 'Key Implementation Steps',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., 1. Set up Intersection Observer\\n2. Handle pagination\\n3. Optimize with React.memo',
          tooltip: 'List the main coding steps',
          group: 'content'
        },
        {
          id: 'technicalExplanation',
          aiVariable: 'technicalExplanation',
          label: 'How It Works (Brief)',
          type: 'textarea',
          required: false,
          placeholder: 'e.g., We observe the last element and trigger batch fetches',
          group: 'content'
        },
        {
          id: 'targetAudience',
          aiVariable: 'targetAudience',
          label: 'Target Audience Level',
          type: 'select',
          required: false,
          options: [
            { value: 'beginners', label: 'Beginners' },
            { value: 'intermediate', label: 'Intermediate' },
            { value: 'advanced', label: 'Advanced' }
          ],
          group: 'basics'
        },
        {
          id: 'codeRepoLink',
          aiVariable: 'codeRepoLink',
          label: 'Code Repository Link (optional)',
          type: 'text',
          required: false,
          placeholder: 'e.g., github.com/you/repo',
          group: 'cta'
        }
      ]
    }
  },

  {
    id: 'dev-api-docs',
    title: 'API Documentation',
    description: 'Document your API with overview, authentication, endpoints, and examples',
    category: 'developer',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'API Overview',
        suggestedDuration: 45,
        layoutPreset: 'Preset_CamTopRight',
        background: { type: 'solid', colors: ['#1E293B'] },
        scriptKey: 'api-overview',
        notes: 'High-level API intro. State what it does, base URL, versioning. Show API docs on screen. Clear and structured delivery.',
        tags: ['intro', 'content']
      },
      {
        order: 1,
        title: 'Authentication',
        suggestedDuration: 60,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#0F172A'] },
        scriptKey: 'api-auth',
        notes: 'Explain auth method (API key, OAuth, JWT, etc.). Show code example. Mention security best practices. Technical and detailed.',
        tags: ['content']
      },
      {
        order: 2,
        title: 'Key Endpoints',
        suggestedDuration: 90,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#0F172A'] },
        scriptKey: 'api-endpoints',
        notes: 'List 3-5 main endpoints. Show method, path, parameters. Use table or list on screen. Methodical pacing.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Example Usage',
        suggestedDuration: 60,
        layoutPreset: 'Preset_CamBottomRight',
        background: { type: 'solid', colors: ['#111827'] },
        scriptKey: 'api-examples',
        notes: 'Show complete working example (curl, JS, Python, etc.). Run it live if possible. Point to docs for more. Practical and helpful.',
        tags: ['demo', 'outro']
      }
    ],
    scripts: [
      {
        key: 'api-overview',
        sceneTitle: 'API Overview',
        content: 'This is the [API Name] API. It allows you to [Core Functionality]. Base URL: [URL].',
        defaultExample: 'This is the Payments API. It allows you to process transactions, manage subscriptions, and handle refunds. Base URL: api.example.com/v1.',
        teleprompterSettings: { speed: 140, fontSize: 22 }
      },
      {
        key: 'api-auth',
        sceneTitle: 'Authentication',
        content: 'Authentication uses [Method]. Include your API key in the [Header/Query]. Example: [Code].',
        defaultExample: 'Authentication uses Bearer tokens. Include your API key in the Authorization header. Example: Authorization: Bearer sk_live_abc123.',
        teleprompterSettings: { speed: 130, fontSize: 20 }
      },
      {
        key: 'api-endpoints',
        sceneTitle: 'Key Endpoints',
        content: 'Key endpoints: [Endpoint 1] for [Purpose], [Endpoint 2] for [Purpose], [Endpoint 3] for [Purpose].',
        defaultExample: 'Key endpoints: POST /charges for creating payments, GET /subscriptions for listing subscriptions, POST /refunds for processing refunds.',
        teleprompterSettings: { speed: 130, fontSize: 20 }
      },
      {
        key: 'api-examples',
        sceneTitle: 'Example Usage',
        content: 'Here\'s a complete example: [Show Code]. This creates [Result]. Check the docs for more examples.',
        defaultExample: 'Here\'s a complete example: curl -X POST with JSON payload. This creates a $50 charge. Check the docs for more examples.',
        teleprompterSettings: { speed: 140, fontSize: 22 }
      }
    ],
    exampleOutput: {
      title: '4-5 minute API documentation video',
      duration: 255,
      whatUserGets: 'Complete API docs: overview → auth → endpoints → examples'
    },
    backgroundImage: '/templates/backgrounds/api-docs-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'apiName',
          aiVariable: 'apiName',
          label: 'API Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., Payments API, User Management API',
          tooltip: 'The name of your API',
          group: 'basics'
        },
        {
          id: 'coreFunctionality',
          aiVariable: 'coreFunctionality',
          label: 'What does your API do?',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., Process payments, manage subscriptions, handle refunds',
          tooltip: 'Describe the main capabilities of your API',
          group: 'basics'
        },
        {
          id: 'baseUrl',
          aiVariable: 'baseUrl',
          label: 'Base URL',
          type: 'text',
          required: false,
          placeholder: 'e.g., api.yourcompany.com/v1',
          group: 'basics'
        },
        {
          id: 'authMethod',
          aiVariable: 'authMethod',
          label: 'Authentication Method',
          type: 'select',
          required: true,
          options: [
            { value: 'api-key', label: 'API Key' },
            { value: 'bearer-token', label: 'Bearer Token' },
            { value: 'oauth2', label: 'OAuth 2.0' },
            { value: 'basic-auth', label: 'Basic Auth' },
            { value: 'jwt', label: 'JWT' }
          ],
          group: 'content'
        },
        {
          id: 'keyEndpoints',
          aiVariable: 'keyEndpoints',
          label: 'Key Endpoints (3-5)',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., POST /charges - create payment\\nGET /subscriptions - list subs\\nPOST /refunds - process refund',
          tooltip: 'List your main API endpoints with their purposes',
          group: 'content'
        },
        {
          id: 'targetDevelopers',
          aiVariable: 'targetDevelopers',
          label: 'Target Developers',
          type: 'select',
          required: false,
          options: [
            { value: 'beginners', label: 'Beginners' },
            { value: 'intermediate', label: 'Intermediate' },
            { value: 'advanced', label: 'Advanced' },
            { value: 'all-levels', label: 'All Levels' }
          ],
          group: 'basics'
        }
      ]
    }
  },

  // ============ INTERVIEW CATEGORY (2 templates) ============
  {
    id: 'interview-intro',
    title: 'Candidate Introduction',
    description: 'Introduce yourself professionally for interviews or team intros',
    category: 'interview',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'Hello & Name',
        suggestedDuration: 15,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#3B82F6', '#1E40AF'] },
        scriptKey: 'interview-hello',
        notes: 'Professional but warm greeting. Smile, make eye contact. State full name clearly. Express gratitude for opportunity.',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Background',
        suggestedDuration: 60,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'interview-background',
        notes: 'Share relevant experience concisely. Focus on accomplishments, not just duties. Use specific examples and metrics. Confident tone.',
        tags: ['content']
      },
      {
        order: 2,
        title: 'Skills & Strengths',
        suggestedDuration: 45,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'interview-skills',
        notes: 'Highlight 3 key strengths relevant to role. Be specific, not generic. Show passion for your area of expertise. Professional and authentic.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Why This Role',
        suggestedDuration: 40,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#10B981', '#059669'] },
        scriptKey: 'interview-why',
        notes: 'Connect your skills to company needs. Show research and genuine interest. End with enthusiasm. Positive and confident close.',
        tags: ['cta', 'outro']
      }
    ],
    scripts: [
      {
        key: 'interview-hello',
        sceneTitle: 'Hello & Name',
        content: 'Hi, I\'m [Your Name]. Thank you for this opportunity.',
        defaultExample: 'Hi, I\'m Taylor Kim. Thank you for this opportunity to introduce myself.',
        teleprompterSettings: { speed: 150, fontSize: 26 }
      },
      {
        key: 'interview-background',
        sceneTitle: 'Background',
        content: 'I have [X years] experience in [Field]. Most recently, I [Recent Role/Achievement].',
        defaultExample: 'I have 6 years experience in software engineering. Most recently, I led the platform team at TechCo, scaling our infrastructure to handle 10M daily users.',
        teleprompterSettings: { speed: 140, fontSize: 24 }
      },
      {
        key: 'interview-skills',
        sceneTitle: 'Skills & Strengths',
        content: 'My key strengths are [Skill 1], [Skill 2], and [Skill 3]. I\'m particularly passionate about [Area].',
        defaultExample: 'My key strengths are system design, team leadership, and performance optimization. I\'m particularly passionate about building scalable architectures.',
        teleprompterSettings: { speed: 140, fontSize: 24 }
      },
      {
        key: 'interview-why',
        sceneTitle: 'Why This Role',
        content: 'I\'m excited about this role because [Reason]. I believe my experience in [Area] would help [Goal].',
        defaultExample: 'I\'m excited about this role because it combines technical challenges with team leadership. I believe my experience in scaling systems would help drive your growth initiatives.',
        teleprompterSettings: { speed: 140, fontSize: 24 }
      }
    ],
    exampleOutput: {
      title: '2-3 minute candidate introduction',
      duration: 160,
      whatUserGets: 'Professional intro: greeting → background → skills → fit'
    },
    backgroundImage: '/templates/backgrounds/candidate-intro-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'candidateName',
          aiVariable: 'candidateName',
          label: 'Your Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., Taylor Kim',
          group: 'basics'
        },
        {
          id: 'yearsExperience',
          aiVariable: 'yearsExperience',
          label: 'Years of Experience',
          type: 'text',
          required: true,
          placeholder: 'e.g., 6 years',
          group: 'basics'
        },
        {
          id: 'field',
          aiVariable: 'field',
          label: 'Field/Industry',
          type: 'text',
          required: true,
          placeholder: 'e.g., Software Engineering',
          group: 'basics'
        },
        {
          id: 'recentAchievement',
          aiVariable: 'recentAchievement',
          label: 'Recent Role/Achievement',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., Led the platform team at TechCo, scaling to 10M daily users',
          group: 'content'
        },
        {
          id: 'keyStrengths',
          aiVariable: 'keyStrengths',
          label: 'Key Strengths (3)',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., System design, team leadership, performance optimization',
          group: 'content'
        },
        {
          id: 'whyThisRole',
          aiVariable: 'whyThisRole',
          label: 'Why This Role Excites You',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., Combines technical challenges with team leadership',
          group: 'cta'
        }
      ]
    }
  },

  {
    id: 'interview-remote-setup',
    title: 'Remote Interview Setup',
    description: 'Conduct structured remote interviews with welcome, questions, and closing',
    category: 'interview',
    aspectRatio: '16:9',
    scenes: [
      {
        order: 0,
        title: 'Welcome',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#3B82F6', '#1E40AF'] },
        scriptKey: 'remote-welcome',
        notes: 'Warm welcome to candidate. Introduce yourself and company. Set comfortable tone. Outline interview structure briefly.',
        tags: ['intro']
      },
      {
        order: 1,
        title: 'Interview Questions',
        suggestedDuration: 600,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'remote-questions',
        notes: 'Ask prepared questions. Give candidate time to think. Active listening - nod, take notes. Share screen if needed for technical questions.',
        tags: ['content']
      },
      {
        order: 2,
        title: 'Candidate Questions',
        suggestedDuration: 180,
        layoutPreset: 'Preset_Default',
        background: { type: 'solid', colors: ['#1F2937'] },
        scriptKey: 'remote-candidate-q',
        notes: 'Invite questions from candidate. Answer honestly and thoroughly. Share relevant context about team/role. Open and transparent.',
        tags: ['content']
      },
      {
        order: 3,
        title: 'Next Steps',
        suggestedDuration: 30,
        layoutPreset: 'Preset_WebcamFull',
        background: { type: 'gradient', colors: ['#10B981', '#059669'] },
        scriptKey: 'remote-next',
        notes: 'Thank candidate for time. Outline next steps and timeline clearly. Provide contact info. Professional and appreciative close.',
        tags: ['outro']
      }
    ],
    scripts: [
      {
        key: 'remote-welcome',
        sceneTitle: 'Welcome',
        content: 'Welcome [Candidate Name]! Thanks for joining. I\'m [Your Name] from [Company]. Let\'s get started.',
        defaultExample: 'Welcome Sarah! Thanks for joining. I\'m Morgan from the engineering team at DataFlow. Let\'s get started.',
        teleprompterSettings: { speed: 150, fontSize: 24 }
      },
      {
        key: 'remote-questions',
        sceneTitle: 'Interview Questions',
        content: 'Tell me about [Question 1]. Can you walk me through [Question 2]? How would you approach [Question 3]?',
        defaultExample: 'Tell me about your experience with distributed systems. Can you walk me through a challenging technical problem you solved? How would you approach designing a real-time notification system?',
        teleprompterSettings: { speed: 130, fontSize: 20 }
      },
      {
        key: 'remote-candidate-q',
        sceneTitle: 'Candidate Questions',
        content: 'Do you have any questions for me about the role, team, or company?',
        defaultExample: 'Do you have any questions for me about the role, team culture, or our technical stack?',
        teleprompterSettings: { speed: 140, fontSize: 22 }
      },
      {
        key: 'remote-next',
        sceneTitle: 'Next Steps',
        content: 'Thank you for your time today. We\'ll follow up with next steps by [Timeline].',
        defaultExample: 'Thank you for your time today. We\'ll follow up with next steps by end of week.',
        teleprompterSettings: { speed: 150, fontSize: 24 }
      }
    ],
    exampleOutput: {
      title: '13-14 minute remote interview',
      duration: 840,
      whatUserGets: 'Full interview: welcome → questions → candidate Q&A → next steps'
    },
    backgroundImage: '/templates/backgrounds/remote-setup-bg.jpeg',
    createdBy: 'dadan-official',
    isOfficial: true,
    createdAt: Date.now(),
    customization: {
      inputs: [
        {
          id: 'interviewerName',
          aiVariable: 'interviewerName',
          label: 'Your Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., Morgan',
          group: 'basics'
        },
        {
          id: 'companyName',
          aiVariable: 'companyName',
          label: 'Company Name',
          type: 'text',
          required: true,
          placeholder: 'e.g., DataFlow',
          group: 'basics'
        },
        {
          id: 'roleName',
          aiVariable: 'roleName',
          label: 'Role Being Interviewed For',
          type: 'text',
          required: true,
          placeholder: 'e.g., Senior Software Engineer',
          group: 'basics'
        },
        {
          id: 'interviewQuestions',
          aiVariable: 'interviewQuestions',
          label: 'Interview Questions (3-5)',
          type: 'textarea',
          required: true,
          placeholder: 'e.g., Experience with distributed systems\\nWalk through a challenging problem\\nHow would you design a notification system',
          tooltip: 'List the questions you plan to ask',
          group: 'content'
        },
        {
          id: 'nextStepsTimeline',
          aiVariable: 'nextStepsTimeline',
          label: 'Follow-up Timeline',
          type: 'select',
          required: true,
          options: [
            { value: 'same-day', label: 'Same Day' },
            { value: 'next-day', label: 'Next Day' },
            { value: 'end-of-week', label: 'End of Week' },
            { value: 'next-week', label: 'Next Week' }
          ],
          group: 'cta'
        }
      ]
    }
  }
];

// Category metadata
export const TEMPLATE_CATEGORIES = {
  education: { label: 'Education', icon: '🎓', count: 3 },
  business: { label: 'Business', icon: '💼', count: 3 },
  content: { label: 'Content Creation', icon: '🎥', count: 2 },
  developer: { label: 'Developer', icon: '💻', count: 2 },
  interview: { label: 'Interview', icon: '🎤', count: 2 }
};

export const TOTAL_TEMPLATES = TEMPLATE_SEEDS.length; // 12
